/**
 * Sync engine — processes pending queue items against Snipe-IT API.
 */

import * as db from './db.js';
import { addToHistory } from './db.js';
import * as api from './snipeit.js';
import { showToast } from './toast.js';

let syncing = false;

/**
 * Process all pending queue items sequentially.
 * Returns { succeeded: number, failed: number }.
 */
export async function syncAll() {
  if (syncing) return { succeeded: 0, failed: 0 };
  syncing = true;

  const pending = await db.getPending();
  let succeeded = 0;
  let failed = 0;

  for (const item of pending) {
    await db.updateStatus(item.id, 'syncing');

    try {
      await processItem(item);
      await addToHistory(item);
      await db.remove(item.id);
      succeeded++;
    } catch (err) {
      await db.updateStatus(item.id, 'error', err.message);
      failed++;
    }
  }

  syncing = false;

  if (succeeded > 0 || failed > 0) {
    const parts = [];
    if (succeeded > 0) parts.push(`${succeeded} synced`);
    if (failed > 0) parts.push(`${failed} failed`);
    showToast(parts.join(', '));
  }

  // Dispatch event so UI can update
  window.dispatchEvent(new CustomEvent('queue-updated'));

  return { succeeded, failed };
}

async function processItem(item) {
  switch (item.type) {
    case 'create_asset': {
      const result = await api.createAsset(item.payload);
      // Upload creation image if present
      if (item.imageFile && result?.payload?.id) {
        try {
          await api.uploadAssetFile(result.payload.id, item.imageFile, 'Creation Image');
        } catch {
          // Non-fatal — asset was created, file upload is best-effort
        }
      }
      return result;
    }
    case 'checkout':
      return api.checkoutAsset(item.payload.asset_id, item.payload);
    case 'checkin':
      return api.checkinAsset(item.payload.asset_id, item.payload);
    case 'update_asset':
      return api.updateAsset(item.payload.asset_id, item.payload);
    default:
      throw new Error(`Unknown queue item type: ${item.type}`);
  }
}

/**
 * Process a single operation directly against Snipe-IT without queuing.
 * Throws on failure so the caller can handle the error.
 */
export async function syncDirect(type, payload) {
  return processItem({ type, payload });
}

export function isSyncing() {
  return syncing;
}
